/**
 * Dispatches a type-safe custom event.
 */
export function dispatch<TDetail>(
  eventName: keyof DocumentEventMap, // Use the specific map keyof
  detail?: TDetail,
  options?: Omit<CustomEventInit<TDetail>, "detail"> // Bubbles, cancelable etc.
): boolean {
  const event = new CustomEvent<TDetail>(eventName, {
    detail,
    bubbles: options?.bubbles ?? true, // Sensible defaults
    cancelable: options?.cancelable ?? true,
    composed: options?.composed ?? false,
  });
  return document.dispatchEvent(event);
}

type DocumentEventHandlerMap = {
  // Iterate over each key 'K' found in DocumentEventMap.
  // 'K' will be the event name string (e.g., 'click', 'keydown').
  [K in keyof Partial<DocumentEventMap>]: ( // The value for the key 'K' must be a function... // Define the property structure for each key K
    // ...that takes one argument named 'event'...
    event: DocumentEventMap[K] // ...whose type is looked up in DocumentEventMap using the key K.
    // e.g., if K is 'click', DocumentEventMap[K] is MouseEvent.
  ) => void; // ...and returns void (you can change 'void' if your handlers need to return something).
};

export function register(self: any, entries: DocumentEventHandlerMap) {
  const bindings: { name: string; binding: any }[] = [];
  for (const key in entries) {
    const name = key as keyof DocumentEventMap;
    const handler = entries[name] as any;
    const binding = handler.bind(self);
    document.addEventListener(name, binding as any);
    bindings.push({ name: name, binding: binding });
  }

  return () => {
    bindings.forEach((entry) => {
      document.removeEventListener(entry.name, entry.binding);
    });
  };
}

export function next<T>(name: keyof DocumentEventMap): Promise<T> {
  return new Promise((resolve) => {
    function handle(e: CustomEvent<T>) {
      document.removeEventListener(name, handle as any);
      resolve(e.detail as T);
    }

    document.addEventListener(name, handle as any);
  });
}
